'use client';

import React, { useMemo } from 'react';
import { cn } from '@/lib/cn';
import { Image } from './Image';
import { MiniTag, type MiniTagSeverity } from './MiniTag';

export type ApplicationStatus =
  | 'approved'
  | 'rejected'
  | 'submitted'
  | 'draft'
  | 'in_progress'
  | 'unknown';

/**
 * Shown when the application has no unit image, and for generic
 * applications that aren't tied to a unit at all. Same asset the
 * listing cards fall back to, so the two lists stay consistent.
 */
const PLACEHOLDER_SRC = '/listing-card-placeholder.png';

export interface ApplicationListCellProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /** Status of the application — drives the badge. */
  status: ApplicationStatus;
  /** Address text (primary title). Falls back to "Application" upstream. */
  address: string;
  /** Date text (subtitle) */
  date?: string | null;
  /** Unit thumbnail. Falls back to the placeholder when absent. */
  imageUrl?: string | null;
  /** "View" action. The link is hidden when omitted. */
  onView?: () => void;
  /**
   * "Edit" action. The link is hidden when omitted — which is how
   * approved and rejected applications render, since neither can be
   * edited.
   */
  onEdit?: () => void;
  /** Disables both actions (e.g. while a row is opening). */
  disabled?: boolean;
}

function StatusBadge({ status }: { status: ApplicationStatus }) {
  const { severity, label } = useMemo(() => {
    switch (status) {
      case 'approved':
        return { severity: 'positive' as MiniTagSeverity, label: 'Approved' };
      case 'rejected':
        return { severity: 'high' as MiniTagSeverity, label: 'Rejected' };
      case 'submitted':
        return { severity: 'low' as MiniTagSeverity, label: 'Submitted' };
      case 'in_progress':
        return { severity: 'neutral' as MiniTagSeverity, label: 'Draft' };
      default:
        return { severity: 'neutral' as MiniTagSeverity, label: 'Draft' };
    }
  }, [status]);

  return (
    <MiniTag className="w-fit shrink-0" severity={severity}>
      {label}
    </MiniTag>
  );
}

/** Text link inside the actions row. */
function CellAction({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center justify-center gap-1 py-2',
        'text-body-2-high text-accent whitespace-nowrap',
        'rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        'hover:underline disabled:opacity-50 disabled:cursor-default disabled:no-underline',
      )}
    >
      {label}
    </button>
  );
}

/**
 * ApplicationListCell — one row in /profile/applications.
 *
 * The row itself is NOT clickable. Navigation happens through the
 * explicit View / Edit links, because the two go to different places and
 * editing a submitted application is destructive (it reverts it to a
 * draft), so it has to be a deliberate choice rather than a stray tap on
 * the row.
 *
 * @example
 * ```tsx
 * <ApplicationListCell
 *   status="submitted"
 *   address="402 E 92nd St #11C"
 *   date="Dec 18, 2025"
 *   onView={() => openView(app)}
 *   onEdit={() => confirmEdit(app)}
 * />
 * ```
 */
export function ApplicationListCell({
  status,
  address,
  date,
  imageUrl,
  onView,
  onEdit,
  disabled = false,
  className,
  ...props
}: ApplicationListCellProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-4 rounded-xl bg-primary px-2 pt-3 pb-1',
        disabled && 'opacity-50',
        className,
      )}
      {...props}
    >
      {/* Fixed 68px thumbnail — square, clipped to the cell radius. */}
      <div className="w-[68px] shrink-0 overflow-hidden rounded-xl">
        <Image
          src={imageUrl || PLACEHOLDER_SRC}
          alt=""
          aspectRatio="1:1"
          sizes="68px"
          className="w-full"
        />
      </div>

      {/* min-w-0 lets the address truncate instead of pushing the badge out. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex w-full flex-col gap-1">
          <p className="text-body-2 text-primary truncate">{address}</p>
          {date ? <p className="text-caption text-secondary">{date}</p> : null}
        </div>

        <div className="flex items-center gap-4">
          {onView ? <CellAction label="View" onClick={onView} disabled={disabled} /> : null}
          {onEdit ? <CellAction label="Edit" onClick={onEdit} disabled={disabled} /> : null}
        </div>
      </div>

      <StatusBadge status={status} />
    </div>
  );
}
